import React, {
  FC,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { TypedDocumentNode, useQuery, useSuspenseQuery } from '@apollo/client'
import { Widget as WidgetContainer } from '@factorialco/f0-react/dist/experimental'
import { Kind, OperationTypeNode } from 'graphql'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'

import { track, trackingEvents } from './Tracking'

type WidgetQueryArgs = Record<string, unknown>

type CommonArgs = { employeeId: string }

type IntersectionOf<T> = (T extends any ? (x: T) => any : never) extends (x: infer R) => any
  ? R
  : never

type OmitNever<T> = {
  [K in keyof T as T[K] extends never ? never : K]: T[K]
}

// Utility type to extract the `TVariables` from `WidgetParams` and then compute the intersection
export type ExtractCommonWidgetArgs<
  TWidgets extends ReadonlyArray<WidgetParams | NoQueryWidgetParams>,
> = OmitNever<
  IntersectionOf<
    TWidgets[number] extends WidgetParams<any, infer TVariables, any> ? TVariables : never
  >
>

export interface WidgetParams<
  TQuery = any,
  TVariables extends WidgetQueryArgs = any,
  TPolicyEnum = any,
  TPrepared = TQuery,
> {
  id: string
  document: TypedDocumentNode<TQuery, TVariables>
  component: React.FC<{ data: TPrepared; employeeId: string; onClickWidget?: () => void }>
  emptyComponent?: React.FC<{ data: TPrepared; employeeId: string; onClickWidget?: () => void }>
  prepareData?: (data: TQuery, timeInMs?: number) => Promise<TPrepared> | TPrepared
  isEmpty?: (data: TPrepared) => boolean
  policies?: TPolicyEnum[]
}

export interface NoQueryWidgetParams<TPolicyEnum = any> {
  id: string
  component: React.FC<{ employeeId: string; onClickWidget?: () => void }>
  emptyComponent?: React.FC<{ onClickWidget?: () => void }>
  isEmpty?: () => boolean
  policies?: TPolicyEnum[]
}

export function buildWidget<
  TQuery,
  TVariables extends WidgetQueryArgs,
  TPolicyEnum,
  TPrepared = TQuery,
>(
  widgetParams: WidgetParams<TQuery, TVariables, TPolicyEnum, TPrepared>
): WidgetParams<TQuery, TVariables, TPolicyEnum, TPrepared> {
  return widgetParams
}

export function buildNoQueryWidget<TPolicyEnum>(
  widgetParams: NoQueryWidgetParams<TPolicyEnum>
): NoQueryWidgetParams<TPolicyEnum> {
  return widgetParams
}

export function Widget<TQuery, TVariables extends WidgetQueryArgs, TPolicyEnum, TPrepared = TQuery>(
  props: Readonly<{
    widget:
      | WidgetParams<TQuery, TVariables, TPolicyEnum, TPrepared>
      | NoQueryWidgetParams<TPolicyEnum>
    args?: TVariables & CommonArgs
    fullHeight?: boolean
    useSuspense?: boolean
  }>
) {
  const { setWidgetState } = useWidgetsContext()

  const { widget, fullHeight } = props
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const args = (props?.args ?? { employeeId: '-1' }) as TVariables & CommonArgs
  const calcIsEmpty = widget?.isEmpty
  const [preparedData, setPreparedData] = useState<TPrepared>()
  const isEmpty = preparedData ? calcIsEmpty?.(preparedData) : false

  const widgetId = widget.id

  const timer = useRef(Date.now())

  const document = 'document' in widget ? widget.document : undefined
  const prepareData = 'prepareData' in widget ? widget.prepareData : undefined

  const queryDocument =
    document ||
    ({
      kind: Kind.DOCUMENT,
      definitions: [
        {
          operation: OperationTypeNode.QUERY,
          kind: Kind.OPERATION_DEFINITION,
          name: { kind: Kind.NAME, value: 'test' },
          selectionSet: { kind: Kind.SELECTION_SET, selections: [] },
        },
      ],
    } satisfies TypedDocumentNode<TQuery, TVariables>)

  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const useHook = (props.useSuspense || !document ? useSuspenseQuery : useQuery) as typeof useQuery

  const { data, error } = useHook(queryDocument, {
    variables: args,
    skip: !document,
    fetchPolicy: props.useSuspense ? undefined : 'network-only',
  })

  const loading = !!document && !preparedData && !isEmpty

  const handleClickWidget = useCallback(() => {
    track(trackingEvents.profile.clickOnEmployeeProfileOverviewWidget, {
      widget_identifier: widgetId,
    })
  }, [widgetId])

  useLayoutEffect(() => {
    if (loading) {
      setWidgetState(widgetId, 'loading')
    }
  }, [loading, widgetId])

  useLayoutEffect(() => {
    if (!document || (data && prepareData)) {
      const now = Date.now()
      const timeInMS = !props.useSuspense ? now - timer.current : 0
      setWidgetState(widgetId, 'with_content')
      if (data && prepareData) {
        Promise.resolve(prepareData(data, timeInMS)).then(setPreparedData)
      }
    }
  }, [data, document, prepareData, widgetId, props.useSuspense])

  useEffect(() => {
    if (isEmpty) {
      if (widget.emptyComponent) {
        setWidgetState(widgetId, 'empty_and_shown')
      } else {
        setWidgetState(widgetId, 'empty_and_not_shown')
      }
    }
  }, [isEmpty, widget.emptyComponent, widgetId, setWidgetState])

  if (error) return null
  if (!preparedData && document) {
    return <WidgetContainer.Skeleton height={fullHeight ? 'full' : 'lg'} />
  }
  if (isEmpty) {
    if (widget.emptyComponent) {
      if ('document' in widget) {
        return preparedData ? (
          <widget.emptyComponent
            data={preparedData}
            employeeId={args.employeeId}
            onClickWidget={handleClickWidget}
          />
        ) : null
      } else {
        return <widget.emptyComponent onClickWidget={handleClickWidget} />
      }
    }
    return null
  }

  if (!('document' in widget)) {
    return (
      <ErrorBoundary customCrashComponent={<></>}>
        <widget.component employeeId={args.employeeId} onClickWidget={handleClickWidget} />
      </ErrorBoundary>
    )
  } else if (preparedData) {
    return (
      <ErrorBoundary customCrashComponent={<></>}>
        <widget.component
          data={preparedData}
          employeeId={args.employeeId}
          onClickWidget={handleClickWidget}
        />
      </ErrorBoundary>
    )
  }
}

export type WidgetState =
  | 'with_content'
  | 'loading'
  | 'empty_and_shown'
  | 'empty_and_not_shown'
  | 'error'

type WidgetStates = Record<string, WidgetState>

type Context = {
  widgetStates: WidgetStates
  setWidgetState: (widgetIdentifier: string, state: WidgetState) => void
}

const WidgetsContext = createContext<Context>({
  widgetStates: {},
  setWidgetState: () => {},
})

export type OnWidgetsEndLoadingParams = {
  loadingTime: number
  shownWidgets: string[]
  emptyWidgets: string[]
}

type Props = {
  widgets: (WidgetParams | NoQueryWidgetParams)[]
  onWidgetsEndLoading?: (params: OnWidgetsEndLoadingParams) => void
  children: ReactElement
}

export type WidgetsProviderProps = Omit<Props, 'children'>

export const WidgetsProvider: FC<Props> = ({ widgets, onWidgetsEndLoading, children }) => {
  const time = useRef(performance.now())

  const [widgetStates, setWidgetStates] = useState<WidgetStates>(
    widgets.reduce(
      (obj, widget) => ({
        ...obj,
        [widget.id]: 'loading',
      }),
      {}
    )
  )

  const setWidgetState = useCallback(
    (widgetIdentifier: string, state: WidgetState) => {
      if (widgetStates[widgetIdentifier] === state) return

      setWidgetStates((states) => ({ ...states, [widgetIdentifier]: state }))
    },
    [widgetStates]
  )

  const someWidgetsLoading = Object.values(widgetStates).some((state) => state === 'loading')

  const allWidgetsWithState = widgets.every(
    (widget) => widget.id in widgetStates && widgetStates[widget.id]
  )

  useEffect(() => {
    if (allWidgetsWithState && !someWidgetsLoading) {
      const shownWidgets = Object.keys(widgetStates).filter(
        (widgetId) =>
          widgetStates[widgetId] === 'empty_and_shown' || widgetStates[widgetId] === 'with_content'
      )
      const emptyWidgets = Object.keys(widgetStates).filter(
        (widgetId) =>
          widgetStates[widgetId] === 'empty_and_shown' ||
          widgetStates[widgetId] === 'empty_and_not_shown'
      )
      const millis = performance.now() - time.current
      onWidgetsEndLoading?.({ loadingTime: millis, shownWidgets, emptyWidgets })
    }
  }, [widgetStates, allWidgetsWithState, someWidgetsLoading, onWidgetsEndLoading])

  const value = useMemo(
    () => ({
      widgetStates,
      setWidgetState,
    }),
    [widgetStates, setWidgetState]
  )

  return <WidgetsContext.Provider value={value}>{children}</WidgetsContext.Provider>
}

export const useWidgetsContext = () => useContext(WidgetsContext)
